<template>
    <v-card outlined class="section-card px-3">
        <v-card-text>
            <v-container fluid>
                <v-row>
                    <v-col class="d-flex align-center" cols="3">
                        <div>
                            <h1 v-if="this.externalUser">{{ $t('externalTitle', { name: this.externalUserName }) }}</h1>
                            <h1 v-else>{{ $t('title') }}</h1>
                            <div class="d-flex mt-7">
                                <div>
                                    <h2 v-if="numberOfTrades === 0">N/A</h2>
                                    <h2 v-else>{{ ((winningRatio * 100).toFixed(1) * 1).toString() }}%</h2>
                                    <p>{{ $t('metrics.winRatio') }}</p>
                                </div>
                                <div class="mx-7">
                                    <h2 v-if="numberOfTrades === 0">N/A</h2>
                                    <h2 v-else>1 : {{ (riskRewardRatio.toFixed(2) * 1).toString() }}</h2>
                                    <p>{{ $t('metrics.riskReward') }}</p>
                                </div>
                              <div>
                                  <h2 v-if="numberOfTrades === 0">N/A</h2>
                                  <h2 v-else :class="(swisetRatio > 1)? 'title-green' : 'title-red'">
                                    {{ (swisetRatio.toFixed(2) * 1).toString() }}
                                  </h2>
                                  <p>{{ $t('metrics.swisetRatio') }}</p>
                              </div>
                            </div>
                            <v-btn class="button-full-stats mt-3" large @click="fullStats">
                              <v-icon class="mr-2" color="white" small>fa-chart-pie</v-icon>
                              {{ $t('btnFullStats') }} »
                            </v-btn>
                        </div>
                    </v-col>
                    <v-col cols="9">
                        <h2 v-if="numberOfTrades === 0" class="text-center mt-12">
                          <v-icon class="d-flex align-center justify-center mt-12 mb-5" x-large>fa-grin-beam-sweat</v-icon>
                          {{ $t('noTrades') }}
                        </h2>
                        <apexchart v-else :options="optionsRiskProfit" :series="seriesRiskProfit" height=280 type="bar"/>
                    </v-col>
                </v-row>
                <OverallFilters :external-user="externalUser" :external-user-id="externalUserId" class="mt-n5 mb-n7"/>
            </v-container>
        </v-card-text>
    </v-card>
</template>

<script>
    import OverallFilters from "./components/OverallFilters";
    import VueApexCharts from 'vue-apexcharts'
    import Vue from 'vue';
    import { mapMutations } from 'vuex';

    Vue.use(VueApexCharts);
    Vue.component('apexchart', VueApexCharts);

    export default {
        name: "OverallPerformance",
        components: {
            OverallFilters,
        },
        props: {
            trades: Array,
            externalUser: Boolean,
            externalUserId: Number,
            externalUserName: String,
        },
        data() {
            return {
                // RATIOS
                swisetRatio: 0,
                winningRatio: 0,
                riskRewardRatio: 0,
                numberOfTrades: 0,

                optionsDonutChart: {
                    chart: {
                        id: 'donutChart'
                    },
                    colors:['rgb(0,247,132)', 'rgb(0,179,247)', 'rgb(136,0,247)', 'rgb(208,0,247)'],
                    labels: [],
                },
                optionsRiskProfit: {
                    colors: ['rgb(36,120,81)', '#FEB019'],
                    dataLabels: {
                        enabled: false
                    },
                    stroke: {
                        curve: 'smooth',
                        colors: ['rgba(0, 247, 132, 1)']
                    },
                    fill: {
                        colors: ['rgba(0, 247, 132, 0.2)'],
                        opacity: 0.5,
                        type: 'solid',
                    },
                    title: {
                        text: 'Profitability',
                        align: 'left',
                        style: {
                            fontSize: '14px'
                        }
                    },
                    xaxis: {
                        //type: 'datetime',
                        axisBorder: {
                            show: false
                        },
                        axisTicks: {
                            show: false
                        }
                    },
                    yaxis: {
                        tickAmount: 4,
                        floating: false,

                        labels: {
                            style: {
                                color: '#8e8da4',
                            },
                            offsetY: -7,
                            offsetX: 0,
                        },
                        axisBorder: {
                            show: false,
                        },
                        axisTicks: {
                            show: false
                        }
                    },
                    tooltip: {
                        x: {
                            format: "dd/MM/yyyy HH:mm",
                        },
                        fixed: {
                            enabled: false,
                            position: 'topRight'
                        }
                    },
                    grid: {
                        yaxis: {
                            lines: {
                                offsetX: -30
                            }
                        },
                        padding: {
                            left: 20
                        }
                    },
                },

                // Series of charts
                seriesDonut: [],
                seriesRiskProfit: [],
            };
        },
        beforeMount() {
            this.init();
        },
        watch: {
            trades() {
                this.init();
            },
        },
        methods: {
            init() {
              this.setGraphs();
              let numberOfTrades = 0;
              let positiveTrades = 0;
              let negativeTrades = 0;
              let positiveSum = 0;
              let negativeSum = 0;
              let copyOfTrades = this.trades.slice();
              copyOfTrades.forEach((trade)=>{
                if(trade.netProfit > 0) {
                  positiveSum += trade.netProfit;
                  positiveTrades++;
                } else {
                  negativeSum += trade.netProfit;
                  negativeTrades++;
                }
                numberOfTrades++;
              });

              // Global Swiset Ratio Calculation
              this.riskRewardRatio = (positiveTrades === 0 || negativeTrades === 0 || negativeSum === 0) ? 4 :
                  Math.abs((positiveSum / positiveTrades) / (negativeSum / negativeTrades));
              this.winningRatio = positiveTrades / numberOfTrades;
              this.swisetRatio = (this.winningRatio * this.riskRewardRatio) + this.winningRatio;
              this.numberOfTrades = numberOfTrades;
            },
            setGraphs() {
                this.setProfitChart();
                //this.setAssetDistributionChart();
            },
            setProfitChart() {
                let profitSeries = [];
                let riskSeries = [];
                let datesRR = [];
                let sortedTrades = this.trades;
                let index = 0;

                sortedTrades.forEach((trade) => {
                    index++;

                    // Risk/Reward
                    profitSeries.push(trade.netProfit);
                    riskSeries.push(trade.monetaryRisk);
                    //datesRR.push(trade.openTime.getTime());
                    datesRR.push(index);
                });

                this.seriesRiskProfit = [
                    { name: 'Profit', data: profitSeries, },
                ];

                this.optionsRiskProfit = {
                    chart: {
                        //stacked: true,
                        toolbar: {
                            show: true
                        },
                        zoom: {
                            enabled: true
                        }
                    },
                    colors: ['rgb(36,120,81)', '#FEB019'],
                    responsive: [{
                        breakpoint: 480,
                        options: {
                            legend: {
                                position: 'bottom',
                                offsetX: -10,
                                offsetY: 0
                            }
                        }
                    }],
                    plotOptions: {
                        bar: {
                            colors: {
                                ranges: [{
                                    from: -100,
                                    to: -46,
                                    color: '#F15B46'
                                }, {
                                    from: -10000000,
                                    to: 0,
                                    color: '#F15B46'
                                }]
                            },
                            columnWidth: '80%',
                            //endingShape: 'rounded',
                        }
                    },
                    dataLabels: {
                        enabled: false
                    },
                    yaxis: {
                        labels: {
                            formatter: function (value) {
                                return `$${value.toFixed(1)}`;
                            },
                            style: {
                                color: '#6C7079',
                            },
                        },
                    },
                    xaxis: {
                        //type: 'datetime',
                        categories: datesRR,
                        labels: {
                          formatter: function (value) {
                            return ((value * 1) > 3) ? `${value.toFixed(0)}th` :
                                ((value * 1) === 1)? `${value.toFixed(0)}st` :
                                    ((value * 1) === 2)? `${value.toFixed(0)}nd` : `${value.toFixed(0)}rd`;
                          },
                        }
                    },
                    legend: {
                        position: 'right',
                        offsetY: 40
                    },
                    fill: {
                        opacity: 1
                    }
                };
            },
            setAssetDistributionChart() {
                let map = {};
                this.optionsDonutChart.labels = [];
                this.seriesDonut = [];
                let newLabels = [];

                this.trades.forEach((trade) => {
                    if(!(trade.asset.ticker in map))
                        map[trade.asset.ticker] = 1;
                    else
                        map[trade.asset.ticker] = ((map[trade.asset.ticker] * 1) + 1);
                });

                for(let asset in map){
                    newLabels.push(asset);
                    this.seriesDonut.push(map[asset]);
                }

                this.optionsDonutChart = {
                    labels: newLabels,
                    chart: {
                        id: 'donutChart'
                    },
                    colors:['rgb(0,85,50)', 'rgb(36,120,81)', 'rgb(0,196,105)', 'rgba(0, 247, 132, 1)', '#95f9ca'],
                };
            },
            ...mapMutations(['setTradesPageable']),
            fullStats() {
                this.setTradesPageable(null);
                this.$router.push({
                    name: 'full-stats',
                    params: { externalUser: this.externalUser, externalUserId: this.externalUserId }
                });
            },
            reset() {
                this.$emit('reset');
            },
            fromDateChanged(fromDate){
                this.$emit('fromDateChanged', fromDate);
            },
            toDateChanged(toDate){
                this.$emit('toDateChanged', toDate);
            },
            sideChanged(side) {
                this.$emit('sideChanged', side);
            },
            brokerChanged(broker) {
                this.$emit('brokerChanged', broker);
            },
            assetChanged(asset) {
                this.$emit('assetChanged', asset);
            },
            moodChanged(mood) {
                this.$emit('moodChanged', mood);
            },
            strategyChanged(strategy) {
                this.$emit('strategyChanged', strategy);
            },
        },
    }
</script>

<i18n>
{
  "en": {
    "title": "Journal",
    "externalTitle": "{name}'s Journal",
    "metrics": {
      "riskReward": "Risk/Reward Ratio",
      "winRatio": "Win Ratio",
      "swisetRatio": "Swiset Ratio"
    },
    "noTrades": " No trades in the current selection",
    "btnFullStats": "See Full Stats"
  },
  "es": {
    "title": "Bitácora",
    "externalTitle": "Bitácora de {name}",
    "metrics": {
      "riskReward": "Ratio Riesgo/Beneficio",
      "winRatio": "Ratio de Ganancia",
      "swisetRatio": "Ratio Swiset"
    },
    "noTrades": " No hay trades en el filtro actual",
    "btnFullStats": "Ir a Estadísticas"
  }
}
</i18n>

<style lang="scss" scoped>
@import "../../global";

    .button-full-stats {
      border: 2px solid $black-color;
      background-color: $black-color !important;
      color: $white-color !important;
    }

    .title-green{
        color:var(--v-success-base);
    }

    .title-red{
        color: var(--v-error-base);
    }


</style>
